import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import NutritionSetupWizard from '../components/NutritionSetupWizard';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Plus, Apple, Flame, Droplets, Scale, Search, Target,
  TrendingUp, TrendingDown, Minus, Settings, ChevronRight,
  Coffee, Sun, Moon as MoonIcon, Cookie, Sparkles, Lock,
  LineChart, Calendar, ShoppingCart, Edit2, Trash2, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart as RechartsLine, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const MEAL_ICONS = {
  breakfast: Coffee,
  lunch: Sun,
  dinner: MoonIcon,
  snack: Cookie
};

const NutritionProPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasAccess, setHasAccess] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  
  // Data states
  const [targets, setTargets] = useState(null);
  const [todayLogs, setTodayLogs] = useState([]);
  const [todayTotals, setTodayTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [hydration, setHydration] = useState({ total_oz: 0, target_oz: 100 });
  const [weightLogs, setWeightLogs] = useState([]);
  const [weightGoal, setWeightGoal] = useState(null);
  
  // Dialogs
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [showLogWeight, setShowLogWeight] = useState(false);
  const [showAddMeal, setShowAddMeal] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState('breakfast');
  
  // Food search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedFoods, setSelectedFoods] = useState([]);
  
  // Weight log
  const [newWeight, setNewWeight] = useState('');
  
  // Quick Add Foods by meal type
  const [quickAddFoods, setQuickAddFoods] = useState({});
  const [showQuickAddManager, setShowQuickAddManager] = useState(false);
  const [showGroceryList, setShowGroceryList] = useState(false);
  const [groceryList, setGroceryList] = useState([]);
  const [newGroceryItem, setNewGroceryItem] = useState('');

  // Default quick add foods by meal type
  const DEFAULT_QUICK_FOODS = {
    breakfast: [
      { name: 'Eggs (2 large)', calories: 140, protein: 12, carbs: 1, fat: 10 },
      { name: 'Oatmeal (1 cup)', calories: 150, protein: 5, carbs: 27, fat: 3 },
      { name: 'Greek Yogurt', calories: 130, protein: 17, carbs: 6, fat: 4 },
      { name: 'Banana', calories: 105, protein: 1, carbs: 27, fat: 0 },
      { name: 'Protein Shake', calories: 200, protein: 25, carbs: 10, fat: 5 },
      { name: 'Toast w/ Peanut Butter', calories: 190, protein: 7, carbs: 20, fat: 10 }
    ],
    lunch: [
      { name: 'Grilled Chicken Breast', calories: 165, protein: 31, carbs: 0, fat: 4 },
      { name: 'Brown Rice (1 cup)', calories: 215, protein: 5, carbs: 45, fat: 2 },
      { name: 'Mixed Salad', calories: 50, protein: 2, carbs: 10, fat: 0 },
      { name: 'Turkey Sandwich', calories: 350, protein: 25, carbs: 35, fat: 12 },
      { name: 'Quinoa Bowl', calories: 280, protein: 12, carbs: 40, fat: 8 },
      { name: 'Tuna Salad', calories: 200, protein: 30, carbs: 5, fat: 8 }
    ],
    dinner: [
      { name: 'Grilled Salmon', calories: 280, protein: 35, carbs: 0, fat: 15 },
      { name: 'Steak (6oz)', calories: 350, protein: 42, carbs: 0, fat: 18 },
      { name: 'Pasta w/ Marinara', calories: 380, protein: 12, carbs: 65, fat: 8 },
      { name: 'Grilled Vegetables', calories: 80, protein: 3, carbs: 15, fat: 2 },
      { name: 'Sweet Potato', calories: 115, protein: 2, carbs: 27, fat: 0 },
      { name: 'Chicken Stir Fry', calories: 320, protein: 28, carbs: 25, fat: 12 }
    ],
    snack: [
      { name: 'Protein Bar', calories: 200, protein: 20, carbs: 20, fat: 8 },
      { name: 'Apple', calories: 95, protein: 0, carbs: 25, fat: 0 },
      { name: 'Almonds (1oz)', calories: 165, protein: 6, carbs: 6, fat: 14 },
      { name: 'String Cheese', calories: 80, protein: 7, carbs: 1, fat: 5 },
      { name: 'Rice Cakes (2)', calories: 70, protein: 1, carbs: 15, fat: 0 },
      { name: 'Trail Mix (1/4 cup)', calories: 175, protein: 5, carbs: 15, fat: 12 }
    ]
  };

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/features/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // For now, grant access to all users (can be gated later)
      setHasAccess(true); // response.data.nutrition_pro
      
      if (true) { // response.data.nutrition_pro
        await fetchAllData();
      }
    } catch (error) {
      console.error('Failed to check access:', error);
      setHasAccess(true); // Default to allow for now
      await fetchAllData();
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('aura_token');
    
    try {
      // Fetch targets
      const targetsRes = await axios.get(`${API}/nutrition/targets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTargets(targetsRes.data);
      setNeedsSetup(targetsRes.data.needs_setup);
      
      // Fetch today's nutrition logs using LOCAL date for proper daily reset
      const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
      const logsRes = await axios.get(`${API}/nutrition?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayLogs(logsRes.data);
      
      // Calculate totals
      const totals = logsRes.data.reduce((acc, log) => ({
        calories: acc.calories + (log.total_calories || 0),
        protein: acc.protein + (log.total_protein || 0),
        carbs: acc.carbs + (log.total_carbs || 0),
        fat: acc.fat + (log.total_fat || 0)
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
      setTodayTotals(totals);
      
      // Fetch hydration from unified API
      try {
        const hydrationRes = await axios.get(`${API}/hydration/today`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHydration({
          today_oz: hydrationRes.data.total_oz || 0,
          target_oz: hydrationRes.data.goal_oz || 100,
          logs: hydrationRes.data.logs || []
        });
      } catch (e) {
        setHydration({ today_oz: 0, target_oz: 100, logs: [] });
      }
      
      // Load custom quick foods from localStorage or backend
      const savedQuickFoods = localStorage.getItem('aura_quick_foods');
      if (savedQuickFoods) {
        setQuickAddFoods(JSON.parse(savedQuickFoods));
      } else {
        setQuickAddFoods(DEFAULT_QUICK_FOODS);
      }
      
      // Fetch grocery list
      try {
        const groceryRes = await axios.get(`${API}/nutrition/grocery-list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGroceryList(groceryRes.data.items || []);
      } catch (e) {
        // Grocery list may not exist yet
        setGroceryList([]);
      }
      
      // Fetch weight logs
      const weightRes = await axios.get(`${API}/nutrition/weight-log?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeightLogs(weightRes.data.logs || []);
      
      // Fetch weight goal
      const goalRes = await axios.get(`${API}/nutrition/weight-goal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeightGoal(goalRes.data);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const searchFood = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/nutrition/food-search?query=${encodeURIComponent(searchQuery)}&limit=20`);
      setSearchResults(response.data.foods || []);
    } catch (error) {
      toast.error('Failed to search foods');
    } finally {
      setSearchLoading(false);
    }
  };

  const addFoodToMeal = (food) => {
    setSelectedFoods(prev => [...prev, { ...food, servings: 1 }]);
  };

  const removeFoodFromMeal = (index) => {
    setSelectedFoods(prev => prev.filter((_, i) => i !== index));
  };

  const saveMeal = async () => {
    if (selectedFoods.length === 0) {
      toast.error('Add at least one food item');
      return;
    }
    
    try {
      const token = localStorage.getItem('aura_token');
      const foods = selectedFoods.map(f => ({
        name: f.name,
        calories: Math.round(f.calories * f.servings),
        protein: Math.round(f.protein * f.servings * 10) / 10,
        carbs: Math.round(f.carbs * f.servings * 10) / 10,
        fat: Math.round(f.fat * f.servings * 10) / 10,
        servings: f.servings
      }));
      
      await axios.post(`${API}/nutrition`, {
        meal_type: selectedMealType,
        foods: foods,
        date: new Date().toLocaleDateString('en-CA') // Local date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Meal logged!');
      setShowAddMeal(false);
      setSelectedFoods([]);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to log meal');
    }
  };

  // Quick Add a predefined food
  const quickAddFood = async (food) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/nutrition`, {
        meal_type: selectedMealType,
        foods: [{
          name: food.name,
          calories: food.calories,
          protein: food.protein,
          carbs: food.carbs,
          fat: food.fat,
          servings: 1
        }],
        date: new Date().toLocaleDateString('en-CA') // Local date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${food.name} added to ${selectedMealType}!`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add food');
    }
  };

  // Save custom quick foods
  const saveQuickFoods = (mealType, foods) => {
    const updated = { ...quickAddFoods, [mealType]: foods };
    setQuickAddFoods(updated);
    localStorage.setItem('aura_quick_foods', JSON.stringify(updated));
    toast.success('Quick add foods updated!');
  };

  // Add item to grocery list
  const addGroceryItem = async () => {
    if (!newGroceryItem.trim()) return;
    
    const token = localStorage.getItem('aura_token');
    const newList = [...groceryList, { name: newGroceryItem.trim(), checked: false }];
    
    try {
      await axios.post(`${API}/nutrition/grocery-list`, {
        items: newList
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroceryList(newList);
      setNewGroceryItem('');
      toast.success('Item added to grocery list');
    } catch (error) {
      // Save locally anyway
      setGroceryList(newList);
      setNewGroceryItem('');
    }
  };

  // Toggle grocery item checked
  const toggleGroceryItem = async (index) => {
    const token = localStorage.getItem('aura_token');
    const newList = groceryList.map((item, i) => 
      i === index ? { ...item, checked: !item.checked } : item
    );
    
    try {
      await axios.post(`${API}/nutrition/grocery-list`, {
        items: newList
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {}
    
    setGroceryList(newList);
  };

  // Remove grocery item
  const removeGroceryItem = async (index) => {
    const token = localStorage.getItem('aura_token');
    const newList = groceryList.filter((_, i) => i !== index);
    
    try {
      await axios.post(`${API}/nutrition/grocery-list`, {
        items: newList
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {}
    
    setGroceryList(newList);
    toast.success('Item removed');
  };

  const logWater = async (amount) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/hydration/log`, {
        amount_oz: amount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`+${amount}oz water logged`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to log water');
    }
  };

  const logWeight = async () => {
    if (!newWeight) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/nutrition/weight-log`, {
        weight: parseFloat(newWeight)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Weight logged!');
      setShowLogWeight(false);
      setNewWeight('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to log weight');
    }
  };

  const handleSetupComplete = (newTargets) => {
    setTargets(newTargets);
    setNeedsSetup(false);
    setShowSetup(false);
    fetchAllData();
  };

  if (loading) {
    return (
      <DashboardLayout title="Nutrition Pro">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-aura-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  // Show setup wizard if needed
  if (needsSetup || showSetup) {
    return (
      <DashboardLayout title="Nutrition Pro Setup">
        <NutritionSetupWizard 
          onComplete={handleSetupComplete}
          existingData={targets}
        />
      </DashboardLayout>
    );
  }

  // Calculate progress percentages
  const caloriePercent = targets?.calories?.daily_goal 
    ? Math.min(100, Math.round((todayTotals.calories / targets.calories.daily_goal) * 100))
    : 0;
  const proteinPercent = targets?.macros?.protein
    ? Math.min(100, Math.round((todayTotals.protein / targets.macros.protein) * 100))
    : 0;
  const hydrationPercent = hydration.target_oz
    ? Math.min(100, Math.round((hydration.total_oz / hydration.target_oz) * 100))
    : 0;

  return (
    <DashboardLayout title="Nutrition Pro">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              Nutrition System
            </h1>
            <p className="text-aura-text-muted text-sm mt-1">Fuel your body for performance.</p>
            <p className="text-aura-text-muted mt-1">
              Smart tracking for {targets?.settings?.weight_goal === 'lose' ? 'weight loss' : 
                targets?.settings?.weight_goal === 'gain' ? 'muscle gain' : 'maintenance'}
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowSetup(true)}
            className="border-white/20 text-aura-text-muted"
          >
            <Settings className="w-4 h-4 mr-2" />
            Recalculate
          </Button>
        </div>

        {/* Main Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Calories Card */}
          <Card className="bg-gradient-to-br from-red-500/20 to-orange-500/10 border-red-500/30">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Flame className="w-5 h-5 text-red-400" />
                  <span className="text-sm text-aura-text-muted uppercase">Calories</span>
                </div>
                <Badge className={`border-0 ${
                  caloriePercent < 80 ? 'bg-yellow-500/20 text-yellow-400' :
                  caloriePercent > 110 ? 'bg-red-500/20 text-red-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {caloriePercent}%
                </Badge>
              </div>
              <p className="text-3xl font-bold text-white">
                {todayTotals.calories}
                <span className="text-lg text-aura-text-muted ml-1">
                  / {targets?.calories?.daily_goal || 2500}
                </span>
              </p>
              <Progress value={caloriePercent} className="h-2 mt-3" />
              <p className="text-xs text-aura-text-muted mt-2">
                {targets?.calories?.daily_goal - todayTotals.calories > 0 
                  ? `${targets?.calories?.daily_goal - todayTotals.calories} remaining`
                  : `${todayTotals.calories - targets?.calories?.daily_goal} over`}
              </p>
            </CardContent>
          </Card>

          {/* Protein Card */}
          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border-blue-500/30">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Apple className="w-5 h-5 text-blue-400" />
                  <span className="text-sm text-aura-text-muted uppercase">Protein</span>
                </div>
                <Badge className={`border-0 ${
                  proteinPercent < 80 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {proteinPercent}%
                </Badge>
              </div>
              <p className="text-3xl font-bold text-white">
                {Math.round(todayTotals.protein)}g
                <span className="text-lg text-aura-text-muted ml-1">
                  / {targets?.macros?.protein || 150}g
                </span>
              </p>
              <Progress value={proteinPercent} className="h-2 mt-3" />
              <p className="text-xs text-aura-text-muted mt-2">
                {targets?.protein?.per_lb}g per lb body weight
              </p>
            </CardContent>
          </Card>

          {/* Hydration Card */}
          <Card className="bg-gradient-to-br from-cyan-500/20 to-teal-500/10 border-cyan-500/30">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-cyan-400" />
                  <span className="text-sm text-aura-text-muted uppercase">Hydration</span>
                </div>
                <Badge className={`border-0 ${
                  hydrationPercent < 50 ? 'bg-red-500/20 text-red-400' :
                  hydrationPercent < 80 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {hydrationPercent}%
                </Badge>
              </div>
              <p className="text-3xl font-bold text-white">
                {hydration.total_oz}oz
                <span className="text-lg text-aura-text-muted ml-1">
                  / {hydration.target_oz}oz
                </span>
              </p>
              <Progress value={hydrationPercent} className="h-2 mt-3" />
              <div className="flex gap-2 mt-3">
                {[8, 16, 24].map(oz => (
                  <Button
                    key={oz}
                    size="sm"
                    variant="ghost"
                    onClick={() => logWater(oz)}
                    className="flex-1 text-cyan-400 hover:bg-cyan-500/20"
                  >
                    +{oz}oz
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Macros Summary */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white uppercase">Today's Macros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Protein', value: todayTotals.protein, target: targets?.macros?.protein || 150, color: 'blue', unit: 'g' },
                { label: 'Carbs', value: todayTotals.carbs, target: targets?.macros?.carbs || 300, color: 'yellow', unit: 'g' },
                { label: 'Fat', value: todayTotals.fat, target: targets?.macros?.fat || 80, color: 'purple', unit: 'g' }
              ].map(macro => (
                <div key={macro.label} className="text-center">
                  <div className="relative w-20 h-20 mx-auto mb-2">
                    <svg className="w-20 h-20 -rotate-90">
                      <circle cx="40" cy="40" r="35" stroke="currentColor" strokeWidth="6" fill="none" className="text-white/10" />
                      <circle 
                        cx="40" cy="40" r="35" 
                        stroke="currentColor" 
                        strokeWidth="6" 
                        fill="none" 
                        className={`text-${macro.color}-400`}
                        strokeDasharray={`${(Math.min(macro.value / macro.target, 1) * 220)} 220`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-bold text-white">{Math.round(macro.value)}</span>
                    </div>
                  </div>
                  <p className="text-sm text-aura-text-muted">{macro.label}</p>
                  <p className="text-xs text-aura-text-subtle">/ {macro.target}{macro.unit}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Button 
            onClick={() => { setSelectedMealType('breakfast'); setShowAddMeal(true); }}
            className="h-auto py-4 bg-aura-surface border border-white/10 hover:bg-white/5 flex flex-col gap-2"
          >
            <Coffee className="w-6 h-6 text-yellow-400" />
            <span className="text-white">Breakfast</span>
          </Button>
          <Button 
            onClick={() => { setSelectedMealType('lunch'); setShowAddMeal(true); }}
            className="h-auto py-4 bg-aura-surface border border-white/10 hover:bg-white/5 flex flex-col gap-2"
          >
            <Sun className="w-6 h-6 text-orange-400" />
            <span className="text-white">Lunch</span>
          </Button>
          <Button 
            onClick={() => { setSelectedMealType('dinner'); setShowAddMeal(true); }}
            className="h-auto py-4 bg-aura-surface border border-white/10 hover:bg-white/5 flex flex-col gap-2"
          >
            <MoonIcon className="w-6 h-6 text-purple-400" />
            <span className="text-white">Dinner</span>
          </Button>
          <Button 
            onClick={() => { setSelectedMealType('snack'); setShowAddMeal(true); }}
            className="h-auto py-4 bg-aura-surface border border-white/10 hover:bg-white/5 flex flex-col gap-2"
          >
            <Cookie className="w-6 h-6 text-amber-400" />
            <span className="text-white">Snack</span>
          </Button>
          <Button 
            onClick={() => setShowGroceryList(true)}
            className="h-auto py-4 bg-gradient-to-br from-green-500/20 to-emerald-500/10 border border-green-500/30 hover:border-green-500/50 flex flex-col gap-2"
          >
            <ShoppingCart className="w-6 h-6 text-green-400" />
            <span className="text-white">Grocery List</span>
          </Button>
        </div>

        {/* Goals & Grocery Quick Links */}
        <div className="grid grid-cols-2 gap-3">
          <Card 
            className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30 cursor-pointer hover:border-amber-500/50 transition-colors"
            onClick={() => navigate('/goals')}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Goals & Targets</p>
                <p className="text-xs text-aura-text-muted">Adjust calorie & macro goals</p>
              </div>
              <ChevronRight className="w-5 h-5 text-amber-400" />
            </CardContent>
          </Card>
          <Card 
            className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30 cursor-pointer hover:border-green-500/50 transition-colors"
            onClick={() => navigate('/grocery-list')}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Full Grocery List</p>
                <p className="text-xs text-aura-text-muted">Goal-based shopping list</p>
              </div>
              <ChevronRight className="w-5 h-5 text-green-400" />
            </CardContent>
          </Card>
        </div>

        {/* Weight Tracking Section */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg text-white uppercase flex items-center gap-2">
                <Scale className="w-5 h-5 text-green-400" />
                Weight Tracking
              </CardTitle>
              <Button 
                size="sm" 
                onClick={() => setShowLogWeight(true)}
                className="bg-aura-primary hover:bg-aura-primary-hover"
              >
                <Plus className="w-4 h-4 mr-1" />
                Log Weight
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {weightLogs.length > 0 ? (
              <div className="space-y-4">
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={weightLogs}>
                      <defs>
                        <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                        tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis 
                        domain={['dataMin - 5', 'dataMax + 5']}
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="weight" 
                        stroke="#22c55e" 
                        fill="url(#weightGradient)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white">{weightLogs[weightLogs.length - 1]?.weight}</p>
                    <p className="text-xs text-aura-text-muted">Current (lbs)</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white flex items-center justify-center gap-1">
                      {weightLogs.length > 1 ? (
                        <>
                          {(weightLogs[weightLogs.length - 1]?.weight - weightLogs[0]?.weight).toFixed(1)}
                          {weightLogs[weightLogs.length - 1]?.weight > weightLogs[0]?.weight 
                            ? <TrendingUp className="w-4 h-4 text-green-400" />
                            : weightLogs[weightLogs.length - 1]?.weight < weightLogs[0]?.weight
                            ? <TrendingDown className="w-4 h-4 text-red-400" />
                            : <Minus className="w-4 h-4 text-gray-400" />
                          }
                        </>
                      ) : '—'}
                    </p>
                    <p className="text-xs text-aura-text-muted">Change</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{weightGoal?.target_weight || '—'}</p>
                    <p className="text-xs text-aura-text-muted">Target (lbs)</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Scale className="w-12 h-12 text-aura-text-muted mx-auto mb-3" />
                <p className="text-aura-text-muted">No weight entries yet</p>
                <Button 
                  onClick={() => setShowLogWeight(true)}
                  className="mt-3 bg-aura-primary hover:bg-aura-primary-hover"
                >
                  Log Your First Weight
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Meals */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-white uppercase">Today's Meals</CardTitle>
          </CardHeader>
          <CardContent>
            {todayLogs.length > 0 ? (
              <div className="space-y-3">
                {todayLogs.map((log, idx) => {
                  const Icon = MEAL_ICONS[log.meal_type] || Coffee;
                  return (
                    <div key={log.log_id || idx} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                      <div className="w-10 h-10 bg-aura-primary/20 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-aura-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium capitalize">{log.meal_type}</p>
                        <p className="text-sm text-aura-text-muted">
                          {log.foods?.map(f => f.name).join(', ').slice(0, 50) || 'No items'}
                          {(log.foods?.map(f => f.name).join(', ').length > 50) && '...'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{log.total_calories} cal</p>
                        <p className="text-xs text-aura-text-muted">
                          P: {Math.round(log.total_protein)}g
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Apple className="w-12 h-12 text-aura-text-muted mx-auto mb-3" />
                <p className="text-aura-text-muted">No meals logged today</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Meal Dialog */}
      <Dialog open={showAddMeal} onOpenChange={setShowAddMeal}>
        <DialogContent className="bg-aura-surface border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              {React.createElement(MEAL_ICONS[selectedMealType] || Coffee, { className: "w-5 h-5 text-aura-primary" })}
              Log {selectedMealType}
            </DialogTitle>
          </DialogHeader>
          
          {/* Instructions */}
          <div className="bg-aura-primary/10 border border-aura-primary/30 rounded-lg p-3 -mt-2">
            <p className="text-xs text-aura-primary">
              <span className="font-semibold">How to log:</span> Select foods below, then adjust the number of <span className="underline">servings</span> you ate. 
              Each food shows its calories per serving.
            </p>
          </div>
          
          <div className="space-y-4 pt-2">
            {/* Quick Add Foods for this meal type */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-aura-text-muted uppercase tracking-wider">Quick Add</p>
                  <p className="text-[10px] text-aura-text-muted">Tap to instantly log 1 serving</p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setShowQuickAddManager(true)}
                  className="text-xs text-aura-primary hover:bg-aura-primary/10"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Customize
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(quickAddFoods[selectedMealType] || DEFAULT_QUICK_FOODS[selectedMealType] || []).slice(0, 6).map((food, idx) => (
                  <button
                    key={idx}
                    onClick={() => quickAddFood(food)}
                    className="p-2 bg-white/5 rounded-lg hover:bg-aura-primary/20 hover:border-aura-primary/30 border border-transparent transition-all text-left"
                  >
                    <p className="text-xs text-white font-medium truncate">{food.name}</p>
                    <p className="text-[10px] text-aura-text-muted">{food.calories} cal • {food.protein}g P</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-sm text-aura-text-muted uppercase tracking-wider">Or Search</p>
              <p className="text-[10px] text-aura-text-muted mb-2">Search to add foods with custom servings</p>
            </div>
            
            {/* Search */}
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search foods (e.g., chicken breast)"
                className="flex-1 bg-aura-bg border-white/10 text-white"
                onKeyDown={(e) => e.key === 'Enter' && searchFood()}
              />
              <Button onClick={searchFood} disabled={searchLoading} className="bg-aura-primary">
                {searchLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-2">
                {searchResults.map((food, idx) => (
                  <button
                    key={food.fdc_id || idx}
                    onClick={() => addFoodToMeal(food)}
                    className="w-full p-3 bg-white/5 rounded-lg hover:bg-white/10 text-left transition-colors"
                    data-testid={`search-result-${idx}`}
                  >
                    <p className="text-white font-medium text-sm">{food.name}</p>
                    <p className="text-xs text-aura-text-muted">
                      {food.calories} cal • P: {food.protein}g • C: {food.carbs}g • F: {food.fat}g
                    </p>
                    {food.serving_size && food.serving_unit && (
                      <p className="text-xs text-aura-primary mt-0.5">
                        Per serving: {food.serving_size}{food.serving_unit}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Selected Foods */}
            {selectedFoods.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-aura-text-muted uppercase">Selected Foods</p>
                {selectedFoods.map((food, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-aura-primary/10 rounded-lg border border-aura-primary/30">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{food.name}</p>
                      <p className="text-xs text-aura-text-muted">
                        {Math.round(food.calories * food.servings)} cal
                        {food.serving_size && food.serving_unit && (
                          <span className="text-aura-primary ml-1">
                            • 1 serving = {food.serving_size}{food.serving_unit}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] text-aura-text-muted uppercase mb-1">Servings</span>
                      <Input
                        type="number"
                        value={food.servings}
                        onChange={(e) => {
                          const newFoods = [...selectedFoods];
                          newFoods[idx].servings = parseFloat(e.target.value) || 1;
                          setSelectedFoods(newFoods);
                        }}
                        className="w-20 bg-aura-bg border-white/10 text-white text-center"
                        min="0.25"
                        step="0.25"
                        data-testid={`food-servings-input-${idx}`}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeFoodFromMeal(idx)}
                      className="text-red-400 hover:bg-red-500/20"
                    >
                      ×
                    </Button>
                  </div>
                ))}
                
                {/* Serving Size Helper */}
                <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <p className="text-xs text-blue-300">
                    <span className="font-semibold">Tip:</span> Enter number of servings (e.g., 1.5 = one and a half servings). 
                    Use 0.5 for half a serving, 2 for double portion.
                  </p>
                </div>
                {/* Totals */}
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-sm text-aura-text-muted mb-2">Meal Totals</p>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-white">
                        {Math.round(selectedFoods.reduce((sum, f) => sum + f.calories * f.servings, 0))}
                      </p>
                      <p className="text-xs text-aura-text-muted">Calories</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-blue-400">
                        {Math.round(selectedFoods.reduce((sum, f) => sum + f.protein * f.servings, 0))}g
                      </p>
                      <p className="text-xs text-aura-text-muted">Protein</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-yellow-400">
                        {Math.round(selectedFoods.reduce((sum, f) => sum + f.carbs * f.servings, 0))}g
                      </p>
                      <p className="text-xs text-aura-text-muted">Carbs</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-purple-400">
                        {Math.round(selectedFoods.reduce((sum, f) => sum + f.fat * f.servings, 0))}g
                      </p>
                      <p className="text-xs text-aura-text-muted">Fat</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button 
              onClick={saveMeal}
              disabled={selectedFoods.length === 0}
              className="w-full h-12 bg-aura-primary hover:bg-aura-primary-hover"
            >
              Log Meal
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Weight Dialog */}
      <Dialog open={showLogWeight} onOpenChange={setShowLogWeight}>
        <DialogContent className="bg-aura-surface border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <Scale className="w-5 h-5 text-green-400" />
              Log Weight
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm text-aura-text-muted">Weight (lbs)</label>
              <Input
                type="number"
                value={newWeight}
                onChange={(e) => setNewWeight(e.target.value)}
                placeholder="165"
                className="h-12 bg-aura-bg border-white/10 text-white text-2xl text-center"
                step="0.1"
              />
            </div>
            <Button 
              onClick={logWeight}
              disabled={!newWeight}
              className="w-full h-12 bg-green-600 hover:bg-green-700"
            >
              Save Weight
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grocery List Dialog */}
      <Dialog open={showGroceryList} onOpenChange={setShowGroceryList}>
        <DialogContent className="bg-aura-surface border-white/10 text-white max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-green-400" />
              Grocery List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Add new item */}
            <div className="flex gap-2">
              <Input
                value={newGroceryItem}
                onChange={(e) => setNewGroceryItem(e.target.value)}
                placeholder="Add item..."
                className="flex-1 bg-aura-bg border-white/10 text-white"
                onKeyDown={(e) => e.key === 'Enter' && addGroceryItem()}
              />
              <Button onClick={addGroceryItem} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Grocery items */}
            {groceryList.length > 0 ? (
              <div className="space-y-2">
                {groceryList.map((item, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      item.checked ? 'bg-green-500/10' : 'bg-white/5'
                    }`}
                  >
                    <button
                      onClick={() => toggleGroceryItem(idx)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        item.checked 
                          ? 'bg-green-500 border-green-500' 
                          : 'border-white/30 hover:border-green-500'
                      }`}
                    >
                      {item.checked && <span className="text-white text-xs">✓</span>}
                    </button>
                    <span className={`flex-1 ${item.checked ? 'line-through text-aura-text-muted' : 'text-white'}`}>
                      {item.name}
                    </span>
                    <button
                      onClick={() => removeGroceryItem(idx)}
                      className="text-red-400 hover:text-red-300 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ShoppingCart className="w-12 h-12 text-aura-text-muted mx-auto mb-3" />
                <p className="text-aura-text-muted">Your grocery list is empty</p>
                <p className="text-sm text-aura-text-subtle mt-1">Add items above to get started</p>
              </div>
            )}

            {/* Summary */}
            {groceryList.length > 0 && (
              <div className="pt-3 border-t border-white/10 flex items-center justify-between text-sm">
                <span className="text-aura-text-muted">
                  {groceryList.filter(i => i.checked).length} / {groceryList.length} items
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const unchecked = groceryList.filter(i => !i.checked);
                    setGroceryList(unchecked);
                    toast.success('Checked items cleared');
                  }}
                  className="text-aura-text-muted hover:text-white"
                >
                  Clear Checked
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Add Foods Manager Dialog */}
      <Dialog open={showQuickAddManager} onOpenChange={setShowQuickAddManager}>
        <DialogContent className="bg-aura-surface border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <Settings className="w-5 h-5 text-aura-primary" />
              Customize Quick Add Foods
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-aura-text-muted">
              Customize the quick add foods for each meal type. These appear when you log a meal.
            </p>
            
            <Tabs defaultValue={selectedMealType} className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-white/5">
                <TabsTrigger value="breakfast" className="text-xs">Breakfast</TabsTrigger>
                <TabsTrigger value="lunch" className="text-xs">Lunch</TabsTrigger>
                <TabsTrigger value="dinner" className="text-xs">Dinner</TabsTrigger>
                <TabsTrigger value="snack" className="text-xs">Snack</TabsTrigger>
              </TabsList>
              
              {['breakfast', 'lunch', 'dinner', 'snack'].map(mealType => (
                <TabsContent key={mealType} value={mealType} className="space-y-3">
                  {(quickAddFoods[mealType] || DEFAULT_QUICK_FOODS[mealType] || []).map((food, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm text-white">{food.name}</p>
                        <p className="text-xs text-aura-text-muted">
                          {food.calories} cal • P:{food.protein}g • C:{food.carbs}g • F:{food.fat}g
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const updated = (quickAddFoods[mealType] || DEFAULT_QUICK_FOODS[mealType]).filter((_, i) => i !== idx);
                          saveQuickFoods(mealType, updated);
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  <Button
                    variant="outline"
                    className="w-full border-dashed border-white/20 text-aura-text-muted hover:text-white hover:border-white/40"
                    onClick={() => {
                      const name = prompt('Food name:');
                      if (!name) return;
                      const calories = parseInt(prompt('Calories:') || '0');
                      const protein = parseInt(prompt('Protein (g):') || '0');
                      const carbs = parseInt(prompt('Carbs (g):') || '0');
                      const fat = parseInt(prompt('Fat (g):') || '0');
                      
                      const current = quickAddFoods[mealType] || DEFAULT_QUICK_FOODS[mealType] || [];
                      saveQuickFoods(mealType, [...current, { name, calories, protein, carbs, fat }]);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Food
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full text-aura-text-muted hover:text-white"
                    onClick={() => {
                      saveQuickFoods(mealType, DEFAULT_QUICK_FOODS[mealType]);
                      toast.success('Reset to defaults');
                    }}
                  >
                    Reset to Defaults
                  </Button>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default NutritionProPage;
